import { mixpanelTrackEventAnonymouslyFromBE, mixpanelTrackEventFromBE } from '.';
import { getLocalItem, removeLocalItem, setLocalItem } from '../../common';
import { MIXPANEL_ANONYMOUS_TEMP_HASH_ID } from '../../constants/mixpanel';
import { isEmpty } from '../helpers';
import { EventProperties, MixpanelTrackEventProps } from './mixpanel';

export interface MixpanelTrackEventPropsWithOptionalCallback<T extends keyof EventProperties>
  extends MixpanelTrackEventProps<T> {
  callBack?: () => void;
  otherAccountHashId?: string;
}
export interface MixpanelTrackEventAnonymouslyProps<T extends keyof EventProperties>
  extends MixpanelTrackEventProps<T> {
  tempId?: string;
}

export const mixpanelTrackEvent = async <T extends keyof EventProperties>(
  props: MixpanelTrackEventPropsWithOptionalCallback<T>
): Promise<void> => {
  const { eventName, payload, callBack, otherAccountHashId } = props;

  const tempHashId = getMixpanelAnonymousTempId();

  const eventPayload: any = {
    event_name: eventName,
    payload: {
      event_properties: payload?.eventProperties ?? {},
      user_properties: payload?.userProperties ?? {},
      incremental_properties: payload?.incrementalProperties ?? {},
    },
  };

  if (otherAccountHashId) {
    eventPayload.other_account_hash_id = otherAccountHashId;
  }

  // used this to merge the temp id to the user hash id
  if (tempHashId && !otherAccountHashId) {
    eventPayload.temp_id = tempHashId;
  }

  // remove it immediately
  removeMixpanelAnonymousTempId();

  await mixpanelTrackEventFromBE(eventPayload);

  if (typeof callBack === 'function') {
    callBack();
  }
};

export const mixpanelTrackEventAnonymously = async <T extends keyof EventProperties>(
  props: MixpanelTrackEventAnonymouslyProps<T>
): Promise<void | string> => {
  const tempHashId = getMixpanelAnonymousTempId();

  const { eventName, payload } = props;

  const eventPayload: any = {
    event_name: eventName,
    payload: {
      event_properties: payload?.eventProperties ?? {},
      user_properties: payload?.userProperties ?? {},
      incremental_properties: payload?.incrementalProperties ?? {},
    },
  };

  if (tempHashId) {
    eventPayload.temp_id = tempHashId;
  }

  const resultTempId = await mixpanelTrackEventAnonymouslyFromBE(eventPayload);
  
  setLocalItem(MIXPANEL_ANONYMOUS_TEMP_HASH_ID, resultTempId);
};

export const getMixpanelAnonymousTempId = (): string | null => {
  const existingValues = getLocalItem(MIXPANEL_ANONYMOUS_TEMP_HASH_ID);

  return !isEmpty(existingValues) ? existingValues : null;
};

export const removeMixpanelAnonymousTempId = (): void => {
  removeLocalItem(MIXPANEL_ANONYMOUS_TEMP_HASH_ID);
};
